import {
	AfterContentInit,
	ContentChildren,
	DestroyRef,
	Directive,
	ElementRef,
	inject,
	OnDestroy,
	QueryList
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ResponsiveTableColumnDirective } from './responsive-table-column.directive';
import { DropdownPortalService } from '../../services/dropdown-portal.service';

interface ColumnGroup {
	name: string;
	priority: number;
	elements: HTMLElement[];
}

@Directive({
	selector: '[msResponsiveTable]',
	standalone: true
})
export class ResponsiveTableDirective implements AfterContentInit, OnDestroy {
	private readonly elementRef = inject(ElementRef<HTMLTableElement>);
	private readonly destroyRef = inject(DestroyRef);
	private readonly dropdownPortalService = inject(DropdownPortalService);

	@ContentChildren(ResponsiveTableColumnDirective, { descendants: true })
	private columnDirectives!: QueryList<ResponsiveTableColumnDirective>;

	private resizeObserver?: ResizeObserver;
	private mutationObserver?: MutationObserver;
	private container?: HTMLElement;
	private columnGroups: ColumnGroup[] = [];
	private hiddenColumns = new Set<string>();
	private isEvaluating = false;
	private contentInitialized = false;
	private pendingEvaluation?: number;

	// #region Lifecycle

	public ngAfterContentInit(): void {
		this.container = this.elementRef.nativeElement.closest('.table-responsive') as HTMLElement;
		if (!this.container) {
			this.container = this.elementRef.nativeElement.parentElement as HTMLElement;
		}

		this.buildColumnGroups();
		this.contentInitialized = true;

		this.columnDirectives.changes
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => {
				this.buildColumnGroups();
				this.evaluate();
			});

		this.resizeObserver = new ResizeObserver(() => {
			if (!this.isEvaluating) {
				if (this.pendingEvaluation) {
					cancelAnimationFrame(this.pendingEvaluation);
				}
				this.pendingEvaluation = requestAnimationFrame(() => {
					this.pendingEvaluation = undefined;
					this.evaluate();
				});
			}
		});

		this.resizeObserver.observe(this.container);

		// Watch for dynamically added rows (e.g. @for re-renders) to fix their dropdown strategy
		this.mutationObserver = new MutationObserver(() => this.fixDropdownStrategy());
		this.mutationObserver.observe(this.elementRef.nativeElement, { childList: true, subtree: true });

		// Initial evaluation and dropdown fix after first layout
		this.fixDropdownStrategy();
		requestAnimationFrame(() => this.evaluate());
	}

	public ngOnDestroy(): void {
		if (this.pendingEvaluation) {
			cancelAnimationFrame(this.pendingEvaluation);
		}
		this.resizeObserver?.disconnect();
		this.mutationObserver?.disconnect();

		// Takes a menu portaled out of this table back off <body> before the view goes.
		this.dropdownPortalService.release(this.elementRef.nativeElement);
	}

	// #endregion

	// #region Column Group Management

	private buildColumnGroups(): void {
		const groupMap = new Map<string, ColumnGroup>();

		this.columnDirectives.forEach(col => {
			const name = col.msResponsiveTableColumn();
			const priority = col.priority();
			const existing = groupMap.get(name);

			if (existing) {
				existing.elements.push(col.elementRef.nativeElement);
			} else {
				groupMap.set(name, {
					name,
					priority,
					elements: [col.elementRef.nativeElement]
				});
			}
		});

		this.columnGroups = Array.from(groupMap.values());
	}

	// #endregion

	// #region Responsive Evaluation

	private evaluate(): void {
		if (!this.contentInitialized || !this.container) return;

		const containerWidth = this.container.clientWidth;
		if (containerWidth === 0) return;

		this.isEvaluating = true;

		const table = this.elementRef.nativeElement;

		// Show all columns so we can measure the table's natural (unconstrained) width
		this.showAllColumns();

		// Temporarily remove width:100% so the table renders at its natural width
		// This is synchronous — the browser won't paint until this JS frame completes
		table.style.width = 'auto';

		// Force a synchronous reflow to get the natural width
		const naturalWidth = table.scrollWidth;

		if (naturalWidth <= containerWidth) {
			// Everything fits — restore and done
			table.style.width = '';
			this.isEvaluating = false;
			return;
		}

		// Get hideable columns sorted by priority descending (highest number = hide first)
		// Ties broken by DOM order (rightmost hidden first)
		const hideableColumns = this.columnGroups
			.filter(g => g.priority > 1)
			.sort((a, b) => b.priority - a.priority || this.domOrderTiebreak(b, a));

		// Progressively hide columns, re-measuring after each hide (synchronous reflow)
		for (const column of hideableColumns) {
			this.hideColumn(column.name);

			// Force synchronous reflow to check new width
			const currentWidth = table.scrollWidth;
			if (currentWidth <= containerWidth) break;
		}

		// Restore table width style
		table.style.width = '';

		this.isEvaluating = false;
	}

	// #endregion

	// #region Column Visibility

	private hideColumn(name: string): void {
		if (this.hiddenColumns.has(name)) return;
		this.hiddenColumns.add(name);

		const group = this.columnGroups.find(g => g.name === name);
		group?.elements.forEach(el => el.classList.add('ms-col-hidden'));
	}

	private showAllColumns(): void {
		this.hiddenColumns.clear();
		this.columnGroups.forEach(g => {
			g.elements.forEach(el => el.classList.remove('ms-col-hidden'));
		});
	}

	// #endregion

	// #region Dropdown Fix
	// Escaping the container's clipping is NOT done here any more: DropdownPortalService listens on
	// document and moves any menu opened inside a .table-responsive to <body>, so a list gets that
	// whether or not it uses this directive. Only the positioning strategy is left.

	/**
	 * Adds data-bs-strategy="fixed" to all dropdown toggles inside the table, so Popper positions
	 * the portaled menu against the viewport rather than the document.
	 */
	private fixDropdownStrategy(): void {
		const table = this.elementRef.nativeElement;
		const toggles = table.querySelectorAll('[data-bs-toggle="dropdown"]:not([data-bs-strategy])');
		toggles.forEach((toggle: Element) => {
			toggle.setAttribute('data-bs-strategy', 'fixed');
		});
	}

	// #endregion

	// #region Utilities

	private domOrderTiebreak(a: ColumnGroup, b: ColumnGroup): number {
		const aEl = a.elements[0];
		const bEl = b.elements[0];
		if (!aEl || !bEl) return 0;

		const position = aEl.compareDocumentPosition(bEl);
		if (position & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
		if (position & Node.DOCUMENT_POSITION_PRECEDING) return 1;
		return 0;
	}

	// #endregion
}
